import React, { useEffect } from "react";
import { Formik, Form, FieldArray } from "formik";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import CustomField from "components/form/CustomField";
import FileUpload from "components/common/FileUpload";
import { useSmartBack } from "hooks/useSmartBack";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  getSystemSettings,
  updateSystemSettings,
  uploadHowItWorksVideo,
} from "store/slices/pageContentSlice";
import { appendFormData, handleSubmitForm } from "helpers/apiUtils";
import {
  howItWorksVideoValidationSchema,
  ratingConfigValidationSchema,
} from "utils/validations";
import { Icons } from "assets/icons";
import useConfirmSubmit from "hooks/useConfirmSubmit";
import ConfirmDialog from "components/common/ConfirmDialog";
import PageHeader from "components/common/PageHeader";

const SETTING_TYPES = {
  HOW_IT_WORKS_VIDEO: "how-it-works-video",
  RATING_FEEDBACK: "rating-feedback",
};

const SettingsConfiguration = () => {
  const { t } = useTranslation();
  const goBack = useSmartBack();
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { trash } = Icons || {};

  const type = searchParams.get("type");

  const { systemSettings: fetchedSettings } = useSelector(
    (state) => state.PageContent,
  );

  const systemSettings =
    location.state?.systemSettingsData || fetchedSettings?.data;

  useEffect(() => {
    if (!location.state?.systemSettingsData) {
      dispatch(getSystemSettings());
    }
  }, []);

  const getSetting = (key) => systemSettings?.find((s) => s.key === key);

  const howItWorksVideoSetting = getSetting("howItWorksVideoUrl");
  const ratingConfigSetting = getSetting("ratingConfig");

  if (type === SETTING_TYPES.HOW_IT_WORKS_VIDEO) {
    return (
      <HowItWorksVideoSection
        t={t}
        goBack={goBack}
        dispatch={dispatch}
        navigate={navigate}
        trash={trash}
        setting={howItWorksVideoSetting}
      />
    );
  }

  if (type === SETTING_TYPES.RATING_FEEDBACK) {
    return (
      <RatingFeedbackSection
        t={t}
        goBack={goBack}
        dispatch={dispatch}
        navigate={navigate}
        trash={trash}
        setting={ratingConfigSetting}
      />
    );
  }

  return (
    <div className="page-content">
      <PageHeader title={t("Settings")} />
      <p>{t("Invalid settings section")}</p>
    </div>
  );
};

// ──────────────────── HOW IT WORKS VIDEO SECTION ────────────────────
const HowItWorksVideoSection = ({ t, goBack, dispatch, setting }) => {
  const initialValues = {
    label: {
      en: setting?.value?.label?.en ?? "",
      ar: setting?.value?.label?.ar ?? "",
    },
    duration: setting?.value?.duration ?? "",
    videoUrl: setting?.value?.videoUrl ?? "",
  };

  const handleSubmit = (values) => {
    const formData = new FormData();
    appendFormData(formData, "id", setting?._id);
    formData.append(
      "label",
      JSON.stringify({ en: values.label.en, ar: values.label.ar }),
    );
    appendFormData(formData, "duration", Number(values.duration));
    appendFormData(formData, "video", values.videoUrl);

    handleSubmitForm({
      dispatch,
      action: uploadHowItWorksVideo,
      payload: formData,
      onSuccess: () => goBack(),
    });
  };

  const howItWorksVideo = useConfirmSubmit(handleSubmit);

  return (
    <div className="page-content">
      <h2 className="page_title">{t("How It Works Video")}</h2>
      <h3 className="page_sub_title">
        {t("Configure the video shown to users explaining how the app works")}
      </h3>

      <div className="common_card mt-4">
        <Formik
          initialValues={initialValues}
          enableReinitialize
          validationSchema={howItWorksVideoValidationSchema}
          onSubmit={howItWorksVideo.handleFormSubmit}
        >
          {({ values, setFieldValue, touched, errors }) => (
            <Form>
              <Row className="g-4">
                <Col lg={4}>
                  <div className="form_field">
                    <CustomField
                      label={t("Label (English)")}
                      name="label.en"
                      placeholder={t("Enter label")}
                      required
                      enableTranslate
                      translateTo="ar"
                      targetField="label.ar"
                    />
                  </div>
                </Col>
                <Col lg={4}>
                  <div className="form_field">
                    <CustomField
                      label={t("Label (Arabic)")}
                      name="label.ar"
                      placeholder={t("Enter label")}
                      className="rtl"
                      required
                      enableTranslate
                      translateTo="en"
                      targetField="label.en"
                    />
                  </div>
                </Col>
                <Col lg={4}>
                  <div className="form_field">
                    <CustomField
                      label={t("Duration (Seconds)")}
                      name="duration"
                      type="number"
                      placeholder={t("Enter duration")}
                      required
                    />
                  </div>
                </Col>
                <Col lg={6}>
                  <div className="form_field">
                    <FileUpload
                      label={t("Upload Video")}
                      name="videoUrl"
                      uploadedFiles={values.videoUrl || null}
                      setUploadedFiles={(file) =>
                        setFieldValue("videoUrl", file)
                      }
                      multiple={false}
                      maxFiles={1}
                      maxFileSize={100 * 1024 * 1024}
                      maxUploadSize={100 * 1024 * 1024}
                      acceptedFileTypes={[
                        "video/mp4",
                        "video/quicktime",
                        "video/webm",
                      ]}
                      isShowErrorMsg
                      className={
                        touched?.videoUrl && errors?.videoUrl ? "invalid" : ""
                      }
                    />
                  </div>
                </Col>

                <Col lg={12} className="mt-2 d-flex justify-content-end gap-2">
                  <button
                    type="button"
                    className="main_btn outline"
                    onClick={goBack}
                  >
                    {t("Cancel")}
                  </button>
                  <button type="submit" className="main_btn">
                    {t("Save")}
                  </button>
                </Col>
              </Row>
            </Form>
          )}
        </Formik>
        <ConfirmDialog
          show={howItWorksVideo.showConfirm}
          onCancel={howItWorksVideo.handleCancel}
          onConfirm={howItWorksVideo.handleConfirm}
          title="Save How It Works Video Settings"
          message="Are you sure you want to update the how it works video settings?"
        />
      </div>
    </div>
  );
};

// ──────────────────── RATING & FEEDBACK SECTION ────────────────────
const RatingFeedbackSection = ({ t, goBack, dispatch, trash, setting }) => {
  const initialValues = {
    positiveThreshold: setting?.value?.positiveThreshold ?? "",
    issueTags:
      setting?.value?.issueTags?.map((tag) => ({
        key: tag.key ?? "",
        label: {
          en: tag.label?.en ?? "",
          ar: tag.label?.ar ?? "",
        },
      })) ?? [],
  };

  const handleSubmit = (values) => {
    if (!setting?._id) return;
    handleSubmitForm({
      dispatch,
      action: updateSystemSettings,
      payload: {
        id: setting._id,
        value: {
          positiveThreshold: Number(values.positiveThreshold),
          issueTags: values.issueTags.map((tag) => ({
            key: tag.key,
            label: { en: tag.label.en, ar: tag.label.ar },
          })),
        },
      },
      onSuccess: () => goBack(),
    });
  };

  const ratingConfig = useConfirmSubmit(handleSubmit);

  return (
    <div className="page-content">
      <h2 className="page_title">{t("Rating & Feedback Configuration")}</h2>
      <h3 className="page_sub_title">
        {t("Configure rating thresholds and issue tags shown to customers")}
      </h3>

      <div className="common_card mt-4">
        <Formik
          initialValues={initialValues}
          enableReinitialize
          validationSchema={ratingConfigValidationSchema}
          onSubmit={ratingConfig.handleFormSubmit}
        >
          {({ values }) => (
            <Form>
              <Row className="g-4">
                <Col lg={4}>
                  <div className="form_field">
                    <CustomField
                      label={t("Positive Rating Threshold")}
                      name="positiveThreshold"
                      type="number"
                      placeholder={t("Enter threshold")}
                      infoText={t(
                        "Ratings at or above this value are treated as positive",
                      )}
                      required
                    />
                  </div>
                </Col>
              </Row>

              <h6 className="mt-5">{t("Issue Tags")}</h6>
              <FieldArray name="issueTags">
                {({ push, remove }) => (
                  <>
                    {values.issueTags.map((tag, index) => (
                      <Row className="g-3 mt-1 align-items-center" key={index}>
                        <Col lg={3}>
                          <div className="form_field">
                            <CustomField
                              label={t("Tag Key")}
                              name={`issueTags[${index}].key`}
                              placeholder={t("e.g. wash_quality")}
                              required
                            />
                          </div>
                        </Col>
                        <Col lg={4}>
                          <div className="form_field">
                            <CustomField
                              label={t("Label (English)")}
                              name={`issueTags[${index}].label.en`}
                              placeholder={t("Enter label")}
                              required
                              enableTranslate
                              translateTo="ar"
                              targetField={`issueTags[${index}].label.ar`}
                            />
                          </div>
                        </Col>
                        <Col lg={4}>
                          <div className="form_field">
                            <CustomField
                              label={t("Label (Arabic)")}
                              name={`issueTags[${index}].label.ar`}
                              placeholder={t("Enter label")}
                              className="rtl"
                              required
                              enableTranslate
                              translateTo="en"
                              targetField={`issueTags[${index}].label.en`}
                            />
                          </div>
                        </Col>
                        <Col lg={1} className="d-flex justify-content-center">
                          <img
                            src={trash}
                            alt="trash"
                            width={20}
                            height={20}
                            className="c_p"
                            onClick={() => remove(index)}
                          />
                        </Col>
                      </Row>
                    ))}

                    <Col lg={12} className="mt-3">
                      <button
                        type="button"
                        className="main_btn outline d-flex align-items-center gap-1"
                        onClick={() =>
                          push({ key: "", label: { en: "", ar: "" } })
                        }
                      >
                        {t("Add Issue Tag")}
                      </button>
                    </Col>
                  </>
                )}
              </FieldArray>

              <Row>
                <Col lg={12} className="mt-4 d-flex justify-content-end gap-2">
                  <button
                    type="button"
                    className="main_btn outline"
                    onClick={goBack}
                  >
                    {t("Cancel")}
                  </button>
                  <button type="submit" className="main_btn">
                    {t("Save")}
                  </button>
                </Col>
              </Row>
            </Form>
          )}
        </Formik>
        <ConfirmDialog
          show={ratingConfig.showConfirm}
          onCancel={ratingConfig.handleCancel}
          onConfirm={ratingConfig.handleConfirm}
          title="Save Rating Configuration"
          message="Are you sure you want to update the rating and feedback configuration?"
        />
      </div>
    </div>
  );
};

export default SettingsConfiguration;
